import { type FC, type PropsWithChildren } from 'react';
import { Controller, useWatch, type Control } from 'react-hook-form';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material';

import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';

export type IdentificationFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const IdentificationFormDialog: FC<
  PropsWithChildren<IdentificationFormDialogProps>
> = ({
  open,

  control,

  onClose,
}) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  const { isYearBuiltApproximate } = useWatch({
    control,
    name: 'general',
  });

  return (
    <SubFormDialog title="Identification" open={open} onClose={onClose}>
      <Grid container spacing={2}>
        <Controller
          control={control}
          name="general.organizationName"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                autoFocus
                label="Client Organization"
                type="text"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="general.buildingName"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Building Name"
                type="text"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="general.yearBuilt"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label={`Construction year${isYearBuiltApproximate ? ' (Approximate)' : ''}`}
                type="number"
                variant="outlined"
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="general.isYearBuiltApproximate"
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <FormControlLabel
                label="Approximate construction year"
                control={
                  <Checkbox
                    {...field}
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                }
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>
    </SubFormDialog>
  );
};
